// src/services/request.js

export const request = (path, method, body, stringify = true, useApi = true, timeout = 15000) => {
  if (!path || !method) {
    throw new Error('Both path and method are required for making a request');
  }

  let fetchPath = '';

  // Determine the environment
  const env = process.env.NODE_ENV;
  const buildTarget = process.env.BUILD_TARGET;
  const appPath = process.env.APP_PATH;
  const beUrl = process.env.BE_URL;
  const apiPath = process.env.API_PATH;

  // Set the fetch path based on the environment
  if (env === 'development') {
    fetchPath = `${beUrl}${useApi ? apiPath : ''}${path}`;
  } else if (buildTarget === 'firebase') {
    // use full backend URL in firebase environment file
    fetchPath = `${beUrl}${appPath}${useApi ? apiPath : ''}${path}`;
  } else if (env === 'production') {
    const browserUrl = `${window.location.protocol}//${window.location.hostname}`;
    fetchPath = `${browserUrl}${appPath}${useApi ? apiPath : ''}${path}`;
  }

  const fetchOptions = {
    method,
    credentials: 'include',
    headers: {},
  };

  if (body) {
    fetchOptions.body = stringify ? JSON.stringify(body) : body;
    if (stringify) {
      fetchOptions.headers['Content-Type'] = 'application/json';
    }
  }

  const controller = new AbortController();
  fetchOptions.signal = controller.signal;

  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort(); // Actually cancels the request
      reject(new Error('Request timed out'));
    }, timeout);
  });

  const fetchPromise = fetch(fetchPath, fetchOptions).then(async (res) => {
    clearTimeout(timeoutId);
    const contentType = res.headers.get('Content-Type') || '';
    const isJson = contentType.includes('application/json');

    if (!res.ok) {
      const error = new Error('Request failed');
      error.status = res.status;
      error.response = isJson ? await res.json() : await res.text();
      throw error;
    }

    const data = isJson ? await res.json() : await res.text();
    return { data, headers: res.headers };
  });

  return Promise.race([fetchPromise, timeoutPromise]);
};

export const chatRequest = async (
  messages,
  onChunk,
  conversationId = null,
  model = null,
  stream = true
) => {
  const env = process.env.NODE_ENV;
  const buildTarget = process.env.BUILD_TARGET;
  const appPath = process.env.APP_PATH;
  const beUrl = process.env.BE_URL;
  const apiPath = process.env.API_PATH;
  const chatEndpoint = '/assistant/chat/know-it-all';

  let fetchPath = '';

  // Set the fetch path based on the environment (same logic as request function)
  if (env === 'development') {
    fetchPath = `${beUrl}${apiPath}${chatEndpoint}`;
  } else if (buildTarget === 'firebase') {
    // use full backend URL in firebase environment file
    fetchPath = `${beUrl}${appPath}${apiPath}${chatEndpoint}`;
  } else if (env === 'production') {
    const browserUrl = `${window.location.protocol}//${window.location.hostname}`;
    fetchPath = `${browserUrl}${appPath}${apiPath}${chatEndpoint}`;
  }

  const requestBody = { messages, stream };
  if (model) {
    requestBody.model = model;
  }
  if (conversationId) {
    requestBody.conversationId = conversationId;
  }

  const response = await fetch(fetchPath, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error('Network response was not ok.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Process complete lines (NDJSON format)
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      try {
        const parsed = JSON.parse(trimmed);

        // Pass all events to callback - let component handle them
        onChunk(parsed);
      } catch (err) {
        // Skip malformed JSON lines
        if (!(err instanceof SyntaxError)) {
          console.error('Error parsing stream chunk:', err);
        }
      }
    }
  }

  // Process any remaining data in buffer
  if (buffer.trim()) {
    try {
      const parsed = JSON.parse(buffer.trim());
      onChunk(parsed);
    } catch (err) {
      // Skip malformed JSON in remaining buffer
      if (!(err instanceof SyntaxError)) {
        console.error('Error parsing final stream chunk:', err);
      }
    }
  }
};
